import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  formatE4,
  formatProzent,
  formatTonnen,
  formatVorzeichen,
  parseEuroToE4,
} from "../../shared/money.js";
import {
  hatAbschlag,
  kontraktFuer,
  kontraktmonat,
  kontraktSortierung,
  leseKontraktCode,
  KONTRAKT_TEXT,
  type FrachtErgebnis,
  type PreisErgebnis,
} from "../../shared/muehle.js";
import {
  RHYTHMUS_TEXT,
  monateZwischen,
  type Rhythmus,
  type Vertragsart,
} from "../../shared/kontrakt.js";
import type { PreisfindungStamm, PreisSzenario } from "../../shared/types.js";
import { api } from "../api.js";
import { AnleitungKnopf } from "../AnleitungKnopf.js";
import { Rechenwegtafel } from "../Rechenwegtafel.js";

/**
 * Preisfindung — der Weg vom Weizen zum Mehlpreis, nachvollziehbar.
 *
 * Die Maske zeigt nicht nur das Ergebnis, sondern jeden Schritt dahin. Das
 * ist der eigentliche Zweck: Wer einem Kunden einen Preis nennt, muss ihn
 * begründen können, und wer ihn ändert, muss sehen, woran es lag.
 *
 * Links das Szenario und die Ergebnisse, rechts der Rechenweg als
 * mitlaufende Erklärung (Rechenwegtafel). Die Tabellenzeile, die angeklickt
 * ist, bestimmt den Artikel, an dem der Weg gezeigt wird.
 *
 * Gerechnet wird serverseitig über /api/preisfindung/rechnen — dieselbe
 * Regel wie in der Schnellkalkulation: Notierungen und Parameter kommen aus
 * der Datenbank, damit ein Browserfenster von heute Morgen keinen Preis von
 * gestern nennt.
 */

const MONATE = [
  "Januar",
  "Februar",
  "März",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Dezember",
];

const KEIN_ARTIKEL = "Kein Artikel gewählt — mindestens einen aus der Liste hinzufügen.";

/** Letzter Monat einer zwölfmonatigen Laufzeit, die im Folgemonat beginnt. */
function bisVorgabe(): number {
  const d = new Date();
  return Date.UTC(d.getFullYear(), d.getMonth() + 1 + 11, 1);
}

/**
 * Auswahljahre — genau der Bereich, den `leseSzenario` zulässt
 * (laufendes Jahr − 1 bis + 6).
 *
 * Nicht enger: Mit einem freien von–bis-Paar bestimmt diese Liste, wie lang
 * eine Laufzeit überhaupt sein kann. Bei fünf Jahren wäre die
 * 60-Monats-Grenze aus `berechneAbrufplan` gar nicht erreichbar, und niemand
 * bekäme gesagt, warum.
 */
const JAHRE = Array.from({ length: 8 }, (_, i) => new Date().getFullYear() - 1 + i);

export interface Mischungsanteil {
  sorte: string;
  bezeichnung: string;
  anteil_bp: number;
}

export interface PreisZeile {
  product_id: string;
  sku: string;
  bezeichnung: string;
  ausbeute_bp: number;
  nachprodukt_anteil_bp: number;
  mischung: Mischungsanteil[];
  ergebnis: PreisErgebnis;
  listenpreis_e4: number;
  differenz_e4: number;
}

/** Artikel, die die Herleitung ausgelassen hat — mit Grund. */
export interface Uebersprungen {
  sku: string;
  bezeichnung: string;
  grund: string;
}

interface Antwort {
  szenario: PreisSzenario;
  zeilen: PreisZeile[];
  uebersprungen: Uebersprungen[];
}

interface Staffel {
  entfernung_km: number;
  staffel: FrachtErgebnis[];
}

/** Ein Monat der Kontraktlaufzeit, wie ihn der Server zurueckgibt. */
export interface Monatspreis {
  jahr: number;
  monat: number;
  menge_kg: number;
  kontrakt_name: string;
  preis_je_t_e4: number;
}

export interface KontraktZeile {
  product_id: string;
  sku: string;
  bezeichnung: string;
  ausbeute_bp: number;
  mischung: Mischungsanteil[];
  preis_je_t_e4: number;
  preis_je_100kg_e4: number;
  guenstigster_e4: number;
  teuerster_e4: number;
  kontraktwert_e4: number;
  monatspreise: Monatspreis[];
  listenpreis_e4: number;
  /** Der erste Laufzeitmonat, vollständig gerechnet — für den Rechenweg. */
  vertreter: PreisZeile;
  vertreter_monat: { jahr: number; monat: number };
}

interface KontraktAntwort {
  /** Gilt nur für den ersten Laufzeitmonat — siehe Kommentar in der Route. */
  szenario_vertretermonat: PreisSzenario;
  kontrakt: { von: string; bis: string; gesamt_kg: number; rhythmus: Rhythmus; monate: number };
  perioden: { bezeichnung: string; menge_kg: number; kontrakte: (string | null)[] }[];
  monate: (Monatspreis & { mit_abschlag: boolean; kontrakt_code: string })[];
  zeilen: KontraktZeile[];
  uebersprungen: Uebersprungen[];
  hinweise: string[];
  stand_tage: number | null;
  stand_hinweis_ab: number;
}

/** Letzter Tag eines Monats als ISO-Datum — das Kontraktende. */
const monatsEnde = (jahr: number, monat: number) =>
  new Date(Date.UTC(jahr, monat, 0)).toISOString().slice(0, 10);

const monatsBeginn = (jahr: number, monat: number) =>
  `${jahr}-${String(monat).padStart(2, "0")}-01`;

/**
 * Ergebnis einer Kontraktauskunft.
 *
 * Zwei Tabellen, weil zwei Fragen dahinterstehen: „Was kostet die Ware über
 * die Laufzeit?" und „Woraus setzt sich das zusammen?". Die zweite ist der
 * eigentliche Grund für die ganze Funktion — ein Kontraktpreis, den niemand
 * aufschlüsseln kann, ist ein Bauchgefühl mit Nachkommastellen.
 */
function Kontraktergebnis({
  a,
  rechnet,
  offen,
  setOffen,
}: {
  a: KontraktAntwort;
  rechnet: boolean;
  offen: string | null;
  setOffen: (id: string | null) => void;
}) {
  const alt = a.stand_tage !== null && a.stand_tage >= a.stand_hinweis_ab;

  return (
    <div className="karte">
      <h2>
        Kontraktpreise{" "}
        {rechnet && <span className="hinweistext">· wird berechnet …</span>}
      </h2>

      <p className="hinweistext" style={{ marginTop: 0 }}>
        {formatTonnen(a.kontrakt.gesamt_kg)} t über {a.kontrakt.monate} Monate,{" "}
        {RHYTHMUS_TEXT[a.kontrakt.rhythmus]}. Ein Preis für die Laufzeit, mengengewichtet über
        die Abrufmonate.
      </p>

      {/*
        Der Frischehinweis nennt den ÄLTESTEN der berührten Stände. Vier
        Kontrakte können vier Stände haben; der älteste bestimmt, wie
        belastbar die Auskunft ist.
      */}
      {alt && (
        <p className="meldung hinweis">
          Die älteste benutzte Notierung ist {a.stand_tage} Tage alt (Hinweis ab{" "}
          {a.stand_hinweis_ab}). Bei einer Laufzeit über {formatTonnen(a.kontrakt.gesamt_kg)} t
          bewegen schon wenige Tage Kursdrift einen vierstelligen Kontraktwert — vor dem Angebot
          unter <strong>Marktdaten</strong> nachziehen.
        </p>
      )}

      {a.hinweise.map((h) => (
        <p className="meldung hinweis" key={h}>
          {h}
        </p>
      ))}

      <div className="tabelle-scroll">
        <table>
          <thead>
            <tr>
              <th>Artikel</th>
              <th className="zahl">günstigster Monat €/t</th>
              <th className="zahl">teuerster Monat €/t</th>
              <th className="zahl">Kontraktpreis €/t</th>
              <th className="zahl">je 100 kg</th>
              <th className="zahl">Kontraktwert €</th>
            </tr>
          </thead>
          <tbody>
            {a.zeilen.map((z) => (
              <Fragment key={z.product_id}>
                <tr
                  onClick={() => setOffen(offen === z.product_id ? null : z.product_id)}
                  style={{ cursor: "pointer" }}
                  className={offen === z.product_id ? "pf-gewaehlt" : undefined}
                >
                  <td>
                    {z.bezeichnung}
                    <br />
                    <span className="hinweistext">{z.sku}</span>
                  </td>
                  <td className="zahl">{t(z.guenstigster_e4)}</td>
                  <td className="zahl">{t(z.teuerster_e4)}</td>
                  <td className="zahl">
                    <strong>{t(z.preis_je_t_e4)}</strong>
                  </td>
                  <td className="zahl">{t(z.preis_je_100kg_e4)}</td>
                  <td className="zahl">{t(z.kontraktwert_e4)}</td>
                </tr>
                {offen === z.product_id && (
                  <tr>
                    <td colSpan={6}>
                      <div className="tabelle-scroll">
                        <table className="innentabelle">
                          <thead>
                            <tr>
                              <th>Liefermonat</th>
                              <th>Kontrakt</th>
                              <th className="zahl">Menge t</th>
                              <th className="zahl">Preis €/t</th>
                            </tr>
                          </thead>
                          <tbody>
                            {z.monatspreise.map((m) => (
                              <tr key={`${m.jahr}-${m.monat}`}>
                                <td>
                                  {MONATE[m.monat - 1]} {m.jahr}
                                </td>
                                <td>{m.kontrakt_name}</td>
                                <td className="zahl">{formatTonnen(m.menge_kg)}</td>
                                <td className="zahl">{t(m.preis_je_t_e4)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <h3 style={{ marginTop: "1.4rem" }}>Abrufplan</h3>
      <div className="tabelle-scroll">
        <table>
          <thead>
            <tr>
              <th>Periode</th>
              <th className="zahl">Menge t</th>
              <th>bepreist über</th>
            </tr>
          </thead>
          <tbody>
            {a.perioden.map((p) => (
              <tr key={p.bezeichnung}>
                <td>{p.bezeichnung}</td>
                <td className="zahl">{formatTonnen(p.menge_kg)}</td>
                <td>{p.kontrakte.filter(Boolean).join(" + ")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="hinweistext">
        Überspannt eine Periode zwei Kontrakte, wird sie trotzdem <strong>je Monat</strong>{" "}
        bepreist — ein Quartal einem Kontrakt zuzuschlagen verschöbe die Mengen, die den
        August-Abschlag tragen.
      </p>

      {a.uebersprungen.length > 0 && (
        <>
          <h3 style={{ marginTop: "1.4rem" }}>Nicht herleitbar</h3>
          <ul className="hinweistext">
            {a.uebersprungen.map((u) => (
              <li key={u.sku}>
                {u.bezeichnung} — {u.grund}
              </li>
            ))}
          </ul>
          <p className="hinweistext">
            Ein Artikel kommt nur in den Kontrakt, wenn er in <strong>jedem</strong> Monat der
            Laufzeit herleitbar ist. Fehlt eine einzige Notierung, wäre der gewichtete Preis das
            Mittel über eine Teillaufzeit — plausibel aussehend und falsch.
          </p>
        </>
      )}
    </div>
  );
}

/**
 * €/t aus e4, zwei Nachkommastellen.
 *
 * `formatE4` zeigt bis zu vier — richtig für die Stammdatenpflege, falsch
 * für eine Preistabelle: „368,8028" ist die e4-Auflösung, kein Betrag. Die
 * vollständige Herleitung steht ohnehin im Rechenweg rechts.
 */
const t = (e4: number) =>
  (e4 / 10_000).toLocaleString("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export function Preisfindung({ istAdmin }: { istAdmin: boolean }) {
  const [stamm, setStamm] = useState<PreisfindungStamm | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);

  const [art, setArt] = useState<Vertragsart>("spot");

  /*
   * Vorbelegt mit dem Folgemonat: nach heute gefragt wird selten.
   *
   * Das JAHR gehoert seit den datierten Kontraktcodes dazu. Vorher lief die
   * Rechnung im Dezember auf „Januar" und meinte stillschweigend den Januar
   * des LAUFENDEN Jahres — also einen Monat in der Vergangenheit.
   */
  const [liefermonat, setLiefermonat] = useState(() => (new Date().getMonth() + 1) % 12 + 1);
  const [lieferjahr, setLieferjahr] = useState(
    () => new Date().getFullYear() + (new Date().getMonth() + 1 === 12 ? 1 : 0),
  );

  // Kontraktfelder. Monat und Jahr getrennt statt <input type="month">: Der
  // Typ kommt im Projekt sonst nirgends vor, und die 16-px-Regel ist fuer
  // <select> am Geraet nachgewiesen.
  const [vonMonat, setVonMonat] = useState(() => (new Date().getMonth() + 1) % 12 + 1);
  const [vonJahr, setVonJahr] = useState(
    () => new Date().getFullYear() + (new Date().getMonth() + 1 === 12 ? 1 : 0),
  );
  /*
   * Vorbelegt: zwölf Monate ab dem Folgemonat.
   *
   * `monateZwischen` zählt BEIDE Grenzmonate mit — der letzte Monat ist also
   * der elfte nach dem ersten, nicht der zwölfte. Genau an dieser Stelle
   * verrechnet man sich, und die Maske zeigte anfangs 13 Monate an.
   */
  const [bisMonat, setBisMonat] = useState(() => new Date(bisVorgabe()).getUTCMonth() + 1);
  const [bisJahr, setBisJahr] = useState(() => new Date(bisVorgabe()).getUTCFullYear());
  const [mengeText, setMengeText] = useState("1200");
  const [rhythmus, setRhythmus] = useState<Rhythmus>("monatlich");
  /**
   * Gewählte Artikel. `null` heisst „alle" — nicht die Liste aller IDs.
   *
   * Der Unterschied ist wichtig: Eine materialisierte Liste veraltet, sobald
   * jemand einen Artikel anlegt. `null` schliesst neue automatisch ein und
   * ist zugleich der heutige Zustand, also die richtige Voreinstellung.
   */
  const [auswahl, setAuswahl] = useState<string[] | null>(null);
  /** Gesetzt, solange die Auswahl aus einem Kundensortiment stammt. */
  const [sortimentVon, setSortimentVon] = useState<string | null>(null);

  /** Von Hand geändert — dann stammt die Auswahl nicht mehr vom Kunden. */
  const waehle = useCallback((neu: string[] | null) => {
    setAuswahl(neu);
    setSortimentVon(null);
  }, []);
  const [kontraktAntwort, setKontraktAntwort] = useState<KontraktAntwort | null>(null);
  const [kontraktFehler, setKontraktFehler] = useState<string | null>(null);
  const [kundeId, setKundeId] = useState("");
  const [entfernungText, setEntfernungText] = useState("");
  const [lademengeText, setLademengeText] = useState("25");
  const [mitFracht, setMitFracht] = useState(true);
  const [zahlungszielText, setZahlungszielText] = useState("");

  const [antwort, setAntwort] = useState<Antwort | null>(null);
  const [rechenFehler, setRechenFehler] = useState<string | null>(null);
  const [rechnet, setRechnet] = useState(false);
  /*
   * Getrennt je Betriebsart. Geteilt sprang ein zweiter Klick im
   * Kontraktmodus nicht nur die Monatstabelle zu, sondern auch den Rechenweg
   * zurueck auf den ersten Artikel.
   */
  const [offen, setOffen] = useState<string | null>(null);
  const [offenKontrakt, setOffenKontrakt] = useState<string | null>(null);
  const [staffel, setStaffel] = useState<Staffel | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);

  const laden = useCallback(async () => {
    try {
      setStamm(await api.get<PreisfindungStamm>("/preisfindung"));
      setLadeFehler(null);
    } catch (e) {
      setLadeFehler(e instanceof Error ? e.message : "Laden fehlgeschlagen.");
    }
  }, []);

  useEffect(() => {
    void laden();
  }, [laden]);

  const kunde = useMemo(
    () => stamm?.kunden.find((k) => k.id === kundeId) ?? null,
    [stamm, kundeId],
  );

  /**
   * Die Anfrage als eigenes Memo — sonst hinge der Abrufeffekt an einem
   * Objekt mit neuer Referenz je Render und liefe endlos. Genau dieser
   * Fehler steckte schon einmal in der Schnellkalkulation.
   */
  /*
   * Nur IDs, die es noch gibt.
   *
   * Ändert sich der Artikelbestand, während die Seite steht — zweiter
   * Zugang, zweiter Benutzer —, enthält `auswahl` sonst Karteileichen. Die
   * Karte zeigte dann „Kein Artikel gewählt", der Abrufeffekt fragte aber
   * trotzdem, und der Server antwortete mit „Kein Artikel mit gepflegter
   * Ausbeute und Getreidemischung": eine Meldung, die nach einem
   * Datenproblem klingt und in die Stammdaten schickt.
   */
  const bekannteIds = new Set((stamm?.artikel ?? []).map((a) => a.id));
  const auswahlGueltig = auswahl === null ? null : auswahl.filter((id) => bekannteIds.has(id));

  /** Stabiler Schlüssel für die Abhängigkeitsliste; siehe unten. */
  const auswahlSchluessel =
    auswahlGueltig === null ? "*" : [...auswahlGueltig].sort().join(",");

  const anfrage = useMemo(() => {
    const km = entfernungText.trim();
    return {
      liefermonat,
      lieferjahr,
      produkt_ids: auswahlGueltig,
      kunde_id: kundeId || null,
      entfernung_km: km === "" ? null : Number(km.replace(",", ".")),
      lademenge_kg: Math.round(Number(lademengeText.replace(",", ".")) * 1000) || null,
      mit_fracht: mitFracht,
      zahlungsziel_tage: zahlungszielText.trim() === "" ? null : Number(zahlungszielText),
    };
    // `auswahlSchluessel` statt `auswahl`: Ein Array bekaeme bei jedem
    // Rendern eine neue Referenz, sobald es irgendwo abgeleitet wird — und
    // dann liefe der Abrufeffekt endlos. Der sortierte String ist stabil.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liefermonat, lieferjahr, auswahlSchluessel, kundeId, entfernungText, lademengeText, mitFracht, zahlungszielText]);

  /**
   * Der Kontraktzeitraum als von–bis, dazu die gerechnete Monatszahl.
   *
   * `monateZwischen` zählt beide Grenzmonate mit und gibt 0 zurück, wenn der
   * Zeitraum verdreht ist — daran hängt die Meldung in der Maske, damit der
   * Benutzer nicht erst in eine 400er-Antwort läuft.
   */
  const kontraktZeitraum = useMemo(() => {
    const von = monatsBeginn(vonJahr, vonMonat);
    const bis = monatsEnde(bisJahr, bisMonat);
    const monate = monateZwischen(von, bis);
    return {
      von,
      bis,
      monate,
      einwand:
        monate === 0
          ? "Das Kontraktende liegt vor dem Beginn."
          : monate > 60
            ? `${monate} Monate — mehr als 60 sind nicht vorgesehen.`
            : /*
               * Der Server weist einen Beginn in der Vergangenheit ab; das
               * gehört vor die Serverrunde, wie die beiden Fälle darüber.
               * Sonst zeigt die Maske zufrieden „24 Monate" und die Absage
               * kommt erst aus dem Netz — und der Hinweis am Feld nennt
               * dann auch noch die falsche Ursache („die Kurve reicht bis …").
               */
              vonJahr * 12 + vonMonat < new Date().getFullYear() * 12 + new Date().getMonth() + 1
              ? "Der Zeitraum beginnt in der Vergangenheit — für vergangene Liefermonate gibt es keinen Preis."
              : null,
    };
  }, [vonJahr, vonMonat, bisJahr, bisMonat]);

  const kontraktAnfrage = useMemo(
    () => ({
      ...anfrage,
      von: kontraktZeitraum.von,
      bis: kontraktZeitraum.bis,
      gesamt_kg: Math.round(Number(mengeText.replace(",", ".")) * 1000) || 0,
      rhythmus,
    }),
    [anfrage, kontraktZeitraum, mengeText, rhythmus],
  );

  /*
   * Beim Umschalten die Meldung der anderen Betriebsart wegnehmen. Sonst
   * steht ueber einem fertig gerechneten Kontrakt noch „Entfernung angeben"
   * aus dem Spotlauf — falsche Ursache, falsche Abhilfe.
   */
  useEffect(() => {
    setRechenFehler(null);
    setKontraktFehler(null);
    setMeldung(null);
    /*
     * Auch das ERGEBNIS der anderen Betriebsart. Sonst blieb die Kurvenspanne
     * aus einem Kontraktlauf in der Karte „Grundlagen" stehen, waehrend die
     * Beschriftung daneben schon wieder einen einzelnen Kontrakt nannte:
     * „MATIF Dezember 2026 · 247,50 – 272,00 EUR/t". Beschriftung nennt
     * einen, der Wert acht.
     */
    setAntwort(null);
    setKontraktAntwort(null);
  }, [art]);

  /*
   * Leere Auswahl selbst abfangen. Der Server antwortete sonst mit „Kein
   * Artikel mit gepflegter Ausbeute und Getreidemischung" — eine Meldung,
   * die nach einem Datenproblem klingt und den Benutzer in die Stammdaten
   * schickt, obwohl er nur ein Kästchen setzen muss.
   */
  const nichtsGewaehlt = auswahlGueltig !== null && auswahlGueltig.length === 0;

  /*
   * Das Sortiment des Kunden übernehmen — NUR beim Kundenwechsel.
   *
   * Der Ref ist kein Zierat: `laden()` ersetzt `stamm` nach jedem
   * Übernehmen, und ohne ihn setzte der Effekt die Auswahl danach erneut und
   * verwürfe still, was der Benutzer von Hand geändert hat.
   *
   * Ein Kunde ohne Sortiment lässt die Auswahl unangetastet. Sie
   * zurückzusetzen wäre eine Vermutung darüber, was jemand als Nächstes
   * will — und beim Abwählen eines Kunden die falsche.
   */
  const vorigerKunde = useRef<string>("");
  useEffect(() => {
    if (kundeId === vorigerKunde.current) return;
    vorigerKunde.current = kundeId;

    const k = stamm?.kunden.find((x) => x.id === kundeId);
    if (k?.produkt_ids?.length) {
      setAuswahl(k.produkt_ids);
      setSortimentVon(k.firma);
    } else {
      /*
       * Die AUSWAHL bleibt stehen — sie zurückzusetzen wäre eine Vermutung
       * darüber, was jemand als Nächstes will. Das ETIKETT nicht: Es nannte
       * sonst weiter „Sortiment von Bäckerei Musterbrot", nachdem der Kunde
       * abgewählt war, und behauptete damit eine Zuordnung, die es nicht
       * mehr gibt.
       */
      setSortimentVon(null);
    }
  }, [kundeId, stamm]);

  const kontraktTimer = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (art !== "kontrakt") return;
    // Kein `setKontraktFehler`: Die Karte „Artikel" sagt es bereits an der
    // Stelle, an der man es abstellt. Zweimal, in zwei Farben und zwei
    // Karten, liest sich wie zwei Probleme.
    if (nichtsGewaehlt) {
      setKontraktAntwort(null);
      setKontraktFehler(null);
      setRechnet(false);
      return;
    }
    // Bei verdrehtem oder zu langem Zeitraum gar nicht erst fragen — die
    // Maske sagt es besser als eine 400er-Antwort.
    // Kein `setKontraktFehler`: Der Einwand steht bereits gelb unter dem
    // Feld, das ihn verursacht. Zweimal derselbe Satz in zwei Karten und
    // zwei Farben liest sich wie zwei Probleme.
    if (kontraktZeitraum.einwand) {
      setKontraktAntwort(null);
      setKontraktFehler(null);
      setRechnet(false);
      return;
    }
    /*
     * `abgebrochen` deckt ab, was das Zurücksetzen des Zeitgebers nicht
     * erreicht: eine Anfrage, die beim Umschalten schon unterwegs war. Ohne
     * die Sperre schaltete eine verspätete Antwort der ANDEREN Betriebsart
     * die Anzeige „wird berechnet …" ab oder schriebe ein Ergebnis in eine
     * Maske, die es nicht mehr zeigt.
     */
    let abgebrochen = false;
    window.clearTimeout(kontraktTimer.current);
    kontraktTimer.current = window.setTimeout(() => {
      setRechnet(true);
      void api
        .post<KontraktAntwort>("/preisfindung/kontrakt", kontraktAnfrage)
        .then((a) => {
          if (abgebrochen) return;
          setKontraktAntwort(a);
          setKontraktFehler(null);
        })
        .catch((e) => {
          if (abgebrochen) return;
          setKontraktAntwort(null);
          setKontraktFehler(e instanceof Error ? e.message : "Berechnung fehlgeschlagen.");
        })
        .finally(() => {
          if (!abgebrochen) setRechnet(false);
        });
    }, 300);
    return () => {
      abgebrochen = true;
      window.clearTimeout(kontraktTimer.current);
    };
  }, [art, kontraktAnfrage, kontraktZeitraum.einwand, nichtsGewaehlt]);

  const timer = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (art === "kontrakt") return;
    if (nichtsGewaehlt) {
      setAntwort(null);
      setStaffel(null);
      setRechenFehler(null);
      // Sonst bliebe „wird berechnet …" stehen, wenn beim Leeren gerade
      // eine Anfrage unterwegs war: Der Aufräumer sperrt deren `finally`.
      setRechnet(false);
      return;
    }
    // Siehe Kontrakteffekt oben: dieselbe Sperre gegen verspätete Antworten.
    let abgebrochen = false;
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      setRechnet(true);
      void api
        .post<Antwort>("/preisfindung/rechnen", anfrage)
        .then((a) => {
          if (abgebrochen) return;
          setAntwort(a);
          setRechenFehler(null);
        })
        .catch((e) => {
          if (abgebrochen) return;
          setAntwort(null);
          setRechenFehler(e instanceof Error ? e.message : "Berechnung fehlgeschlagen.");
        })
        .finally(() => {
          if (!abgebrochen) setRechnet(false);
        });

      if (anfrage.mit_fracht)
        void api
          .post<Staffel>("/preisfindung/frachtstaffel", anfrage)
          .then((s) => !abgebrochen && setStaffel(s))
          .catch(() => !abgebrochen && setStaffel(null));
      else setStaffel(null);
    }, 300);
    return () => {
      abgebrochen = true;
      window.clearTimeout(timer.current);
    };
  }, [art, anfrage, nichtsGewaehlt]);

  if (ladeFehler) return <p className="meldung fehler">{ladeFehler}</p>;
  if (!stamm) return <p className="leer">Wird geladen …</p>;

  /*
   * Der Kontraktmonat kommt aus der Tabelle, nicht aus der Antwort des
   * Servers. Sonst zeigt die Maske „nicht hinterlegt" für die Notierung,
   * sobald die Rechnung aus einem ganz anderen Grund scheitert — etwa weil
   * die Entfernung fehlt. Falsche Ursache, falsche Abhilfe.
   */
  const kontrakt = kontraktmonat(liefermonat);
  const kontraktReihe = kontraktFuer(liefermonat, lieferjahr);
  const notierung = stamm.notierungen[kontraktReihe.code] ?? null;
  const diesel = stamm.notierungen.diesel;

  async function uebernehmen() {
    if (!antwort) return;
    const anzahl = antwort.zeilen.filter((z) => z.differenz_e4 !== 0).length;
    if (!anzahl) {
      setMeldung("Die Preisliste steht bereits auf diesen Werten.");
      return;
    }
    if (
      !confirm(
        `${anzahl} Preise in die Preisliste übernehmen?\n\n` +
          "Sie gelten ab sofort für neue Angebote. Bereits gespeicherte Angebote " +
          "bleiben unverändert; jede Änderung wird in der Preishistorie festgehalten.",
      )
    )
      return;

    try {
      const r = await api.post<{ uebernommen: unknown[]; unveraendert: number }>(
        "/preisfindung/uebernehmen",
        { ...anfrage, produkt_ids: antwort.zeilen.map((z) => z.product_id) },
      );
      setMeldung(
        `${r.uebernommen.length} Preise übernommen` +
          (r.unveraendert ? `, ${r.unveraendert} unverändert.` : "."),
      );
      await laden();
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : "Übernahme fehlgeschlagen.");
    }
  }

  /*
   * Der Artikel, an dem die Erklärspalte den Weg zeigt: der angeklickte,
   * sonst der erste. Ein leerer Rechenweg neben einer vollen Tabelle wäre
   * die unfreundlichste Voreinstellung.
   */
  const kontraktZeile =
    art === "kontrakt" && kontraktAntwort
      ? (kontraktAntwort.zeilen.find((z) => z.product_id === offenKontrakt) ??
        kontraktAntwort.zeilen[0] ??
        null)
      : null;

  const bezug = kontraktZeile
    ? // Im Kontraktmodus zeigt die Tafel den Weg an EINEM Monat der Laufzeit.
      // Sechs Schritte mal zwölf Monate wären keine Erklärung mehr.
      kontraktZeile.vertreter
    : antwort
      ? (antwort.zeilen.find((z) => z.product_id === offen) ?? antwort.zeilen[0] ?? null)
      : null;

  /*
   * Die Finanzierungsspalte erscheint nur, wenn sie etwas zeigt.
   *
   * Bedingung aus der ANTWORT, nicht aus `stamm.parameter.zinssatz_bp`: Der
   * Rechenlauf haengt an `anfrage`, das Nachladen der Parameter nicht. Wer
   * den Zinssatz aendert, saehe sonst sofort eine Spalte voller Nullen. Und
   * die Finanzierung ist auch bei Zinssatz > 0 null, wenn das Zahlungsziel
   * null Tage betraegt.
   */
  const zeigtFinanzierung = !!antwort?.zeilen.some((z) => z.ergebnis.finanzierung_e4 !== 0);

  /**
   * Steht für diesen Kontrakt ein KURS — nicht bloss eine Reihe?
   *
   * `ladeNotierungen` ist ein LEFT JOIN: Eine frisch angelegte Reihe ohne
   * Wert liefert einen Eintrag mit `wert_e4: null`, der Schlüssel existiert
   * also. Und genau so legt `pflegeKontraktreihen` jeden neuen Kontrakt an —
   * „Reihe da, Kurs fehlt" ist für den hintersten Kontrakt der Normalfall.
   * Auf den blossen Schlüssel zu prüfen hiesse: keine Warnung, obwohl die
   * Rechnung scheitert.
   */
  const kursDa = (code: string) => stamm.notierungen[code]?.wert_e4 != null;

  /**
   * Bis wann die Terminkurve reicht — der letzte Liefermonat, für den ein
   * Kurs gepflegt ist.
   */
  const kurveReichtBis = (() => {
    const codes = Object.keys(stamm.notierungen)
      .filter(kursDa)
      .map(leseKontraktCode)
      .filter((k): k is NonNullable<typeof k> => !!k)
      .sort((a, b) => kontraktSortierung(a.kontrakt, a.jahr) - kontraktSortierung(b.kontrakt, b.jahr));
    const letzter = codes[codes.length - 1];
    return letzter ? `${KONTRAKT_TEXT[letzter.kontrakt]} ${letzter.jahr}` : null;
  })();

  /*
   * Die gewählten Artikel in der Reihenfolge der Stammdaten, nicht in der
   * des Anklickens — sonst wandert eine Zeile beim Entfernen und
   * Wiederhinzufügen an das Ende der Liste.
   */
  const gewaehlteArtikel =
    auswahl === null ? stamm.artikel : stamm.artikel.filter((a) => auswahl.includes(a.id));
  const fehlendeArtikel = stamm.artikel.filter((a) => !gewaehlteArtikel.includes(a));

  /**
   * Auswahl ändern, aufbauend auf dem JEWEILS aktuellen Stand.
   *
   * Nicht `waehle([...gewaehlteArtikel, x])`: Diese Liste stammt aus dem
   * gerade gerenderten Zustand. Zwei Klicks, bevor React neu rendert —
   * Doppelklick, schnelles Tippen auf dem Telefon — bauen dann beide auf
   * demselben alten Stand auf, und der zweite überschreibt den ersten. Beim
   * Ausprobieren verschwand so eine Entfernung wieder.
   */
  const aendereAuswahl = (f: (jetzt: string[]) => string[]) => {
    const alle = stamm.artikel.map((a) => a.id);
    setAuswahl((a) => f(a ?? alle));
    setSortimentVon(null);
  };

  const endeGedeckt = kursDa(kontraktFuer(bisMonat, bisJahr).code);

  /** Die Kontrakte, die eine Laufzeit berührt — für die Karte „Grundlagen". */
  const kontrakteDerLaufzeit = [
    ...new Set((kontraktAntwort?.monate ?? []).map((m) => m.kontrakt_name)),
  ];

  /**
   * Die Weizennotierung über die Laufzeit als Spanne.
   *
   * Eine einzelne Zahl gäbe es nicht — der Kontraktpreis zieht mehrere
   * Kurven­punkte. Und „nicht hinterlegt" wäre schlicht falsch: Die Kurse
   * SIND gepflegt, es sind nur mehrere.
   */
  const kurvenSpanne = (() => {
    const werte = [...new Set((kontraktAntwort?.monate ?? []).map((m) => m.kontrakt_code))]
      .map((c) => stamm.notierungen[c]?.wert_e4)
      .filter((w): w is number => typeof w === "number");
    if (!werte.length) return null;
    const min = Math.min(...werte);
    const max = Math.max(...werte);
    return min === max ? `${t(min)} EUR/t` : `${t(min)} – ${t(max)} EUR/t`;
  })();

  return (
    <>
      <h1>Preisfindung</h1>
      <p className="hinweistext">
        Weizennotierung → Mehlpreis ab Hof → Fracht → Zahlungsziel. Die Tabelle rechnet in
        <strong> €/Tonne</strong>; die Preisliste führt dieselben Beträge je 100&nbsp;kg. Gemeint
        ist lose Ware, frachtfrei Käuferstation — der Papiersack kommt über die Verpackung hinzu.
      </p>

      <div className="pf-flaeche">
        <div className="pf-haupt">
      {/* ── Szenario ─────────────────────────────────────────────────── */}
      <div className="karte">
        <h2>Szenario</h2>

        {/*
          Spot oder Kontrakt. Der Unterschied ist nicht kosmetisch: Ein
          Spotpreis gilt für einen Liefermonat und einen Kontrakt, ein
          Kontraktpreis für eine ganze Laufzeit und damit für mehrere.
        */}
        <div className="artwahl" role="group" aria-label="Vertragsart">
          {(["spot", "kontrakt"] as const).map((a) => (
            <button
              key={a}
              type="button"
              className={art === a ? "gewaehlt" : undefined}
              aria-pressed={art === a}
              onClick={() => setArt(a)}
            >
              {a === "spot" ? "Einzellieferung" : "Kontrakt über Laufzeit"}
            </button>
          ))}
        </div>

        <div className="raster">
          {art === "spot" ? (
            <div className="feld">
              <label htmlFor="pf-monat">Liefermonat</label>
              <div className="feldpaar">
                <select
                  id="pf-monat"
                  value={liefermonat}
                  onChange={(e) => setLiefermonat(Number(e.target.value))}
                >
                  {MONATE.map((m, i) => (
                    <option key={m} value={i + 1}>
                      {m}
                    </option>
                  ))}
                </select>
                <select
                  aria-label="Lieferjahr"
                  value={lieferjahr}
                  onChange={(e) => setLieferjahr(Number(e.target.value))}
                >
                  {JAHRE.map((j) => (
                    <option key={j} value={j}>
                      {j}
                    </option>
                  ))}
                </select>
              </div>
              <span className="hinweistext">
                Kontrakt {KONTRAKT_TEXT[kontrakt]} {lieferjahr}
                {hatAbschlag(liefermonat) && " · mit Abschlag"}
              </span>
            </div>
          ) : (
            <>
              <div className="feld">
                <label htmlFor="pf-von">Zeitraum von</label>
                <div className="feldpaar">
                  <select
                    id="pf-von"
                    value={vonMonat}
                    onChange={(e) => setVonMonat(Number(e.target.value))}
                  >
                    {MONATE.map((m, i) => (
                      <option key={m} value={i + 1}>
                        {m}
                      </option>
                    ))}
                  </select>
                  <select
                    aria-label="Jahr des Zeitraumbeginns"
                    value={vonJahr}
                    onChange={(e) => setVonJahr(Number(e.target.value))}
                  >
                    {JAHRE.map((j) => (
                      <option key={j} value={j}>
                        {j}
                      </option>
                    ))}
                  </select>
                </div>
                <span className="hinweistext">erster Liefermonat</span>
              </div>

              <div className="feld">
                <label htmlFor="pf-bis">bis</label>
                <div className="feldpaar">
                  <select
                    id="pf-bis"
                    value={bisMonat}
                    onChange={(e) => setBisMonat(Number(e.target.value))}
                  >
                    {MONATE.map((m, i) => (
                      <option key={m} value={i + 1}>
                        {m}
                      </option>
                    ))}
                  </select>
                  <select
                    aria-label="Jahr des Zeitraumendes"
                    value={bisJahr}
                    onChange={(e) => setBisJahr(Number(e.target.value))}
                  >
                    {JAHRE.map((j) => (
                      <option key={j} value={j}>
                        {j}
                      </option>
                    ))}
                  </select>
                </div>
                {/* Die Monatszahl ist die eigentliche Auskunft: Sie zählt
                    beide Grenzmonate mit, und daran verrechnet man sich. */}
                <span className={kontraktZeitraum.einwand ? "feld-warnung" : "hinweistext"}>
                  {kontraktZeitraum.einwand ??
                    `${kontraktZeitraum.monate} ${kontraktZeitraum.monate === 1 ? "Monat" : "Monate"}, einschließlich beider`}
                </span>
                {/* Sagt es vorher statt hinterher: Reicht die Terminkurve
                    nicht bis zum letzten Liefermonat, weist die Rechnung ab
                    — hier steht, woran es liegt und wie weit es geht. */}
                {!kontraktZeitraum.einwand && !endeGedeckt && (
                  <span className="feld-warnung">
                    Für {MONATE[bisMonat - 1]} {bisJahr} gibt es keine Notierung
                    {kurveReichtBis ? ` — die Kurve reicht bis ${kurveReichtBis}` : ""}.
                  </span>
                )}
              </div>

              <div className="feld schmal">
                <label htmlFor="pf-kontraktmenge">Kontraktmenge</label>
                <input
                  id="pf-kontraktmenge"
                  inputMode="decimal"
                  value={mengeText}
                  onChange={(e) => setMengeText(e.target.value)}
                />
                <span className="hinweistext">Tonnen über die gesamte Laufzeit</span>
              </div>

              <div className="feld">
                <label htmlFor="pf-rhythmus">Abruf</label>
                <select
                  id="pf-rhythmus"
                  value={rhythmus}
                  onChange={(e) => setRhythmus(e.target.value as Rhythmus)}
                >
                  {(Object.keys(RHYTHMUS_TEXT) as Rhythmus[]).map((r) => (
                    <option key={r} value={r}>
                      {RHYTHMUS_TEXT[r]}
                    </option>
                  ))}
                </select>
                <span className="hinweistext">
                  {rhythmus === "auf_abruf"
                    ? "ohne feste Monate nicht kalkulierbar"
                    : "bestimmt die Mengen je Periode"}
                </span>
              </div>

              <div className="feld">
                {/* Kein <label>: Es gibt kein Eingabefeld, auf das es zeigen
                    koennte, und ein zielloses Label meldet jeder Pruefer. */}
                <span className="feldtitel">Kurzanleitung</span>
                <AnleitungKnopf />
                <span className="hinweistext">Kontrakte rechnen und Kurse pflegen</span>
              </div>
            </>
          )}

          <div className="feld">
            <label htmlFor="pf-kunde">Kunde</label>
            <select id="pf-kunde" value={kundeId} onChange={(e) => setKundeId(e.target.value)}>
              <option value="">— ohne Kunde —</option>
              {stamm.kunden.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.firma}
                  {k.ort ? ` · ${k.ort}` : ""}
                </option>
              ))}
            </select>
            <span className="hinweistext">
              {kunde
                ? kunde.entfernung_km != null
                  ? `${kunde.entfernung_km} km hinterlegt`
                  : "keine Entfernung hinterlegt"
                : " "}
            </span>
          </div>

          <div className="feld">
            <label htmlFor="pf-km">Entfernung (km, einfach)</label>
            <input
              id="pf-km"
              inputMode="decimal"
              value={entfernungText}
              disabled={!mitFracht}
              placeholder={kunde?.entfernung_km != null ? String(kunde.entfernung_km) : "z. B. 255"}
              onChange={(e) => setEntfernungText(e.target.value)}
            />
          </div>

          <div className="feld">
            <label htmlFor="pf-menge">Lademenge (t)</label>
            <input
              id="pf-menge"
              inputMode="decimal"
              value={lademengeText}
              disabled={!mitFracht}
              onChange={(e) => setLademengeText(e.target.value)}
            />
          </div>

          <div className="feld">
            <label htmlFor="pf-ziel">Zahlungsziel (Tage)</label>
            <input
              id="pf-ziel"
              inputMode="numeric"
              value={zahlungszielText}
              placeholder={String(kunde?.zahlungsziel_tage ?? 14)}
              onChange={(e) => setZahlungszielText(e.target.value)}
            />
            <span className="hinweistext">
              Zinssatz {formatProzent(stamm.parameter.zinssatz_bp ?? 0)}&nbsp;% p.&nbsp;a.
            </span>
          </div>

          <div className="feld">
            <label htmlFor="pf-fracht">Lieferung</label>
            <select
              id="pf-fracht"
              value={mitFracht ? "frei" : "hof"}
              onChange={(e) => setMitFracht(e.target.value === "frei")}
            >
              <option value="frei">frachtfrei Käuferstation</option>
              <option value="hof">ab Hof (Selbstabholung)</option>
            </select>
          </div>
        </div>
        {rechenFehler && (
          <p className="meldung fehler" style={{ marginBottom: 0 }}>
            {rechenFehler}
          </p>
        )}
      </div>

      {/* ── Artikelauswahl ───────────────────────────────────────────── */}
      <div className="karte">
        <h2>Artikel</h2>
        <p className="hinweistext" style={{ marginTop: 0 }}>
          {auswahl === null
            ? `Alle ${stamm.artikel.length} Mahlerzeugnisse. Neue Artikel sind automatisch dabei.`
            : `${gewaehlteArtikel.length} von ${stamm.artikel.length} gewählt.`}
          {sortimentVon && ` · Sortiment von ${sortimentVon}`}
        </p>

        <div className="knopfzeile" style={{ marginTop: 0, alignItems: "center" }}>
          {/*
            Hinzufügen über ein Auswahlfeld, das nur zeigt, was noch fehlt.
            Es springt nach der Wahl auf den Platzhalter zurück, damit man
            mehrere hintereinander wählen kann, ohne dazwischen zu greifen.
          */}
          <select
            aria-label="Artikel hinzufügen"
            value=""
            disabled={!fehlendeArtikel.length}
            onChange={(e) => {
              if (!e.target.value) return;
              const id = e.target.value;
              aendereAuswahl((jetzt) => (jetzt.includes(id) ? jetzt : [...jetzt, id]));
            }}
          >
            <option value="">
              {fehlendeArtikel.length ? "+ Artikel hinzufügen …" : "alle bereits in der Liste"}
            </option>
            {fehlendeArtikel.map((a) => (
              <option key={a.id} value={a.id}>
                {a.bezeichnung} · {a.sku}
              </option>
            ))}
          </select>

          {auswahl !== null && (
            <button type="button" className="knopf schlicht" onClick={() => waehle(null)}>
              alle
            </button>
          )}
          <button type="button" className="knopf schlicht" onClick={() => waehle([])}>
            leeren
          </button>
        </div>

        {gewaehlteArtikel.length ? (
          <ul className="artikelliste">
            {gewaehlteArtikel.map((a) => (
              <li key={a.id}>
                <span>
                  {a.bezeichnung}
                  <span className="hinweistext"> {a.sku}</span>
                </span>
                <button
                  type="button"
                  className="weg"
                  aria-label={`${a.bezeichnung} entfernen`}
                  title="entfernen"
                  onClick={(e) => {
                    /*
                     * Fokus weitergeben, bevor die Zeile verschwindet. Sonst
                     * landet er auf dem <body>, und wer die Liste mit der
                     * Tastatur kürzt, muss sich nach jedem Artikel neu
                     * hineintabben.
                     */
                    const zeile = (e.currentTarget as HTMLElement).closest("li");
                    const naechste = (zeile?.nextElementSibling ??
                      zeile?.previousElementSibling) as HTMLElement | null;
                    (naechste?.querySelector(".weg") as HTMLElement | null)?.focus();
                    aendereAuswahl((jetzt) => jetzt.filter((x) => x !== a.id));
                  }}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="meldung hinweis">{KEIN_ARTIKEL}</p>
        )}
      </div>

      {/* ── Grundlagen ───────────────────────────────────────────────── */}
      <div className="karte">
        <h2>Grundlagen</h2>
        <div className="rechnung">
          {/*
            Je Sorte die Notierung, die sie wirklich benutzt. Vorher stand
            hier fest „Weizennotierung MATIF" — bei einem 100-%-Roggenmehl
            nannte die Karte damit eine Basis, die nicht gilt, und verschwieg
            die, die gilt.
          */}
          {stamm.sorten.map((so) => (
            <Grundlage
              key={so.code}
              bez={
                so.notierung_art === "matif_kontrakt"
                  ? `${so.bezeichnung} · MATIF ${
                      /*
                        Im Kontraktmodus zieht ein Preis mehrere Kontrakte.
                        Nur einen zu nennen behauptete eine Basis, die bloss
                        fuer einen Teil der Laufzeit gilt — und `kontrakt`
                        kaeme aus `liefermonat`, einem Feld, das hier gar
                        nicht sichtbar ist.
                      */
                      art !== "kontrakt"
                        ? `${KONTRAKT_TEXT[kontrakt]} ${lieferjahr}`
                        : kontrakteDerLaufzeit.length
                          ? kontrakteDerLaufzeit.join(", ")
                          : "über die Laufzeit"
                    }`
                  : `${so.bezeichnung} · Notierung`
              }
              n={
                so.notierung_art === "matif_kontrakt"
                  ? // Im Kontraktmodus gibt es keinen Einzelkurs. Ohne dieses
                    // `null` stuende hier der Kurs des Spot-Liefermonats,
                    // samt Standangabe — vollkommen belastbar aussehend und
                    // vom Benutzer nie gewaehlt.
                    art === "kontrakt"
                    ? null
                    : notierung
                  : (stamm.notierungen[so.notierung_serie ?? ""] ?? null)
              }
              praemie={so.praemie_serie ? (stamm.notierungen[so.praemie_serie] ?? null) : null}
              statt={
                art === "kontrakt" && so.notierung_art === "matif_kontrakt"
                  ? (kurvenSpanne ?? "wird berechnet …")
                  : undefined
              }
            />
          ))}
          {mitFracht && <Grundlage bez="Dieselpreis" n={diesel} nachkomma={3} />}
          <div className="rechnung-zeile">
            <span className="bez">Mahllohn</span>
            <span>{t(stamm.parameter.mahllohn_e4 ?? 0)} €/t Weizen</span>
          </div>
          <div className="rechnung-zeile">
            <span className="bez">Erlös Nachprodukte</span>
            <span>{t(stamm.parameter.nachprodukt_preis_e4 ?? 0)} €/t</span>
          </div>
        </div>
        <p className="hinweistext" style={{ marginTop: "0.8rem" }}>
          Notierungen und Prämien werden unter <strong>Marktdaten</strong> gepflegt.
        </p>
      </div>

      {/* ── Ergebnis ─────────────────────────────────────────────────── */}
      {meldung && <p className="meldung hinweis">{meldung}</p>}

      {art === "kontrakt" && kontraktFehler && (
        <p className="meldung fehler">{kontraktFehler}</p>
      )}

      {art === "kontrakt" && kontraktAntwort && (
        <Kontraktergebnis
          a={kontraktAntwort}
          rechnet={rechnet}
          offen={offenKontrakt}
          setOffen={setOffenKontrakt}
        />
      )}

      {art === "spot" && antwort && (
        <div className="karte">
          <h2>
            Mehlpreise{" "}
            {rechnet && <span className="hinweistext">· wird berechnet …</span>}
          </h2>

          {/*
            Die Fracht haengt nur am Szenario, nie am Artikel — als Spalte
            stuende in jeder Zeile dieselbe Zahl. Deshalb einmal darueber.
          */}
          <p className="hinweistext" style={{ marginTop: 0 }}>
            {antwort.zeilen[0]?.ergebnis.fracht_je_t_e4
              ? `Fracht ${t(antwort.zeilen[0].ergebnis.fracht_je_t_e4)} €/t — für alle Artikel gleich, weil sie an der Tour hängt und nicht an der Ware.`
              : "Ab Hof, ohne Fracht."}
          </p>

          <div className="tabelle-scroll">
            <table>
              <thead>
                <tr>
                  <th>Artikel</th>
                  <th className="zahl">Ausbeute</th>
                  <th>Mischung</th>
                  <th className="zahl">ab Hof €/t</th>
                  {zeigtFinanzierung && <th className="zahl">Finanz. €/t</th>}
                  <th className="zahl">Preis €/t</th>
                  <th className="zahl">Liste €/t</th>
                  <th className="zahl">Differenz €/t</th>
                </tr>
              </thead>
              <tbody>
                {antwort.zeilen.map((z) => (
                  <tr
                    key={z.product_id}
                    className={z.product_id === bezug?.product_id ? "pf-gewaehlt" : undefined}
                    onClick={() => setOffen(z.product_id)}
                    style={{ cursor: "pointer" }}
                  >
                    <td>
                      {z.bezeichnung}
                      <br />
                      <span className="hinweistext">{z.sku}</span>
                    </td>
                    <td className="zahl">{formatProzent(z.ausbeute_bp)} %</td>
                    {/*
                      Kein nowrap: Die Zelle waechst sonst mit jeder Sorte —
                      gemessen 125 px bei einer, 227 bei zwei, 320 bei drei.
                      Umbrochen wird zwischen den Bestandteilen.
                    */}
                    <td className="mischung-zelle">
                      {z.mischung
                        .map((x) => `${formatProzent(x.anteil_bp)} % ${x.bezeichnung}`)
                        .join(" · ")}
                    </td>
                    <td className="zahl">{t(z.ergebnis.mehl.ab_hof_e4)}</td>
                    {zeigtFinanzierung && (
                      <td className="zahl">{t(z.ergebnis.finanzierung_e4)}</td>
                    )}
                    <td className="zahl">
                      <strong>{t(z.ergebnis.preis_je_t_e4)}</strong>
                    </td>
                    {/*
                      Die Preisliste steht je 100 kg in der Datenbank; hier
                      wechselt nur die Anzeigeeinheit. Die Differenz wird aus
                      den GEZEIGTEN Tonnenwerten gebildet, damit die drei
                      Spalten sichtbar aufgehen.
                    */}
                    {/*
                      Ohne Listenpreis keine Differenz. Sie wäre der volle
                      hergeleitete Preis und behauptete damit eine Abweichung,
                      die es nicht gibt — bei einem frisch angelegten Artikel
                      stünde dort „+398,32".
                    */}
                    <td className="zahl">
                      {/* Nicht „—": Das steht in der Nachbarspalte für „keine
                          Abweichung". Zwei Bedeutungen nebeneinander. */}
                      {z.listenpreis_e4 > 0 ? t(z.listenpreis_e4 * 10) : "noch keiner"}
                    </td>
                    <td
                      className="zahl"
                      style={{
                        color:
                          z.listenpreis_e4 > 0
                            ? differenzFarbe(z.ergebnis.preis_je_t_e4 - z.listenpreis_e4 * 10)
                            : undefined,
                      }}
                    >
                      {z.listenpreis_e4 <= 0
                        ? "noch keiner"
                        : z.ergebnis.preis_je_t_e4 - z.listenpreis_e4 * 10 === 0
                          ? "—"
                          : formatVorzeichen(z.ergebnis.preis_je_t_e4 - z.listenpreis_e4 * 10)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="hinweistext" style={{ marginTop: "0.6rem" }}>
            Zeile anklicken zeigt ihren Rechenweg rechts.
          </p>

          {/*
            Was fehlt, gehört genannt. Eine Tabelle mit sieben von zwölf
            Artikeln wirft sonst die Frage auf, ob etwas kaputt ist.
          */}
          {antwort.uebersprungen.length > 0 && (
            <p className="hinweistext">
              <strong>Nicht dabei:</strong>{" "}
              {antwort.uebersprungen.map((a) => `${a.bezeichnung} (${a.grund})`).join(" · ")}.
              Ihre Preise bleiben unberührt.
            </p>
          )}

          {istAdmin && (
            <div className="knopfzeile" style={{ marginTop: "1.2rem" }}>
              <button className="knopf" onClick={uebernehmen} disabled={rechnet}>
                Preise in die Preisliste übernehmen
              </button>
              <span className="hinweistext">
                {(() => {
                  const n = antwort.zeilen.filter((z) => z.differenz_e4 !== 0).length;
                  return n === 1
                    ? "Schreibt einen Basispreis neu — mit Historie."
                    : `Schreibt ${n} Basispreise neu — mit Historie.`;
                })()}
              </span>
            </div>
          )}
        </div>
      )}

      {/* ── Frachtstaffel ────────────────────────────────────────────── */}
      {staffel && staffel.staffel.length > 0 && (
        <div className="karte">
          <h2>Frachtstaffel · {staffel.entfernung_km} km einfach</h2>
          <p className="hinweistext">
            Eine Tour kostet, was sie kostet. Wer eine halbe Ladung fährt, zahlt je Tonne das
            Doppelte — deshalb steht die Lademenge in der Rechnung und nicht eine Zone.
          </p>
          <div className="tabelle-scroll">
            <table>
              <thead>
                <tr>
                  <th className="zahl">Lademenge</th>
                  <th className="zahl">Personal</th>
                  <th className="zahl">LKW</th>
                  <th className="zahl">Kraftstoff</th>
                  <th className="zahl">Maut</th>
                  <th className="zahl">Tour</th>
                  <th className="zahl">€/t</th>
                  <th className="zahl">Dieselanteil</th>
                </tr>
              </thead>
              <tbody>
                {staffel.staffel.map((f) => (
                  <tr key={f.lademenge_kg}>
                    <td className="zahl">{formatTonnen(f.lademenge_kg)} t</td>
                    <td className="zahl">{t(f.personal_e4)}</td>
                    <td className="zahl">{t(f.lkw_e4)}</td>
                    <td className="zahl">{t(f.kraftstoff_e4)}</td>
                    <td className="zahl">{t(f.maut_e4)}</td>
                    <td className="zahl">{t(f.tour_e4)}</td>
                    <td className="zahl">
                      <strong>{t(f.je_tonne_e4)}</strong>
                    </td>
                    <td className="zahl">{formatProzent(f.dieselanteil_bp)} %</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {istAdmin && <Parameterpflege stamm={stamm} beiAenderung={laden} />}
        </div>

        <Rechenwegtafel
          stamm={stamm}
          bezug={bezug}
          kontrakt={kontraktZeile ? kontraktmonat(kontraktZeile.vertreter_monat.monat) : kontrakt}
          kontraktJahr={kontraktZeile ? kontraktZeile.vertreter_monat.jahr : lieferjahr}
          mitFracht={mitFracht}
          laufzeit={
            kontraktZeile && kontraktAntwort
              ? {
                  monat: kontraktZeile.vertreter_monat.monat,
                  jahr: kontraktZeile.vertreter_monat.jahr,
                  anzahl: kontraktAntwort.kontrakt.monate,
                  guenstigster_e4: kontraktZeile.guenstigster_e4,
                  teuerster_e4: kontraktZeile.teuerster_e4,
                  preis_je_t_e4: kontraktZeile.preis_je_t_e4,
                  preis_je_100kg_e4: kontraktZeile.preis_je_100kg_e4,
                  kontrakte: [...new Set(kontraktZeile.monatspreise.map((x) => x.kontrakt_name))],
                }
              : null
          }
        />
      </div>
    </>
  );
}

const differenzFarbe = (e4: number) =>
  e4 === 0 ? undefined : e4 > 0 ? "var(--engelke-rot)" : "var(--gruen)";

type Notierungsanzeige = {
  wert_e4: number | null;
  einheit: string;
  quelle_stand: string | null;
  herkunft: string | null;
} | null;

function Grundlage({
  bez,
  n,
  praemie,
  nachkomma = 2,
  statt,
}: {
  bez: string;
  n: Notierungsanzeige;
  /** Wird hinter der Notierung mit Vorzeichen angehängt — sie darf negativ sein. */
  praemie?: Notierungsanzeige;
  nachkomma?: number;
  /**
   * Ersetzt den Einzelwert. Im Kontraktmodus zieht ein Preis mehrere
   * Kurvenpunkte; hier steht dann die Spanne statt einer Zahl, die es so
   * nicht gibt.
   */
  statt?: string;
}) {
  if (statt)
    return (
      <div className="rechnung-zeile">
        <span className="bez">{bez}</span>
        <span>
          {statt}
          {praemie?.wert_e4 != null && ` ${formatVorzeichen(praemie.wert_e4)}`}
        </span>
      </div>
    );

  if (!n?.wert_e4 && n?.wert_e4 !== 0)
    return (
      <div className="rechnung-zeile">
        <span className="bez">{bez}</span>
        <span style={{ color: "var(--engelke-rot)" }}>nicht hinterlegt</span>
      </div>
    );
  return (
    <div className="rechnung-zeile">
      <span className="bez">
        {bez}
        {n.quelle_stand && (
          <span className="hinweistext">
            {" "}
            · Stand {n.quelle_stand.slice(0, 10)}
            {n.herkunft === "manuell" ? " (manuell)" : ""}
          </span>
        )}
      </span>
      <span>
        {(n.wert_e4 / 10_000).toLocaleString("de-DE", {
          minimumFractionDigits: nachkomma,
          maximumFractionDigits: nachkomma,
        })}
        {praemie?.wert_e4 != null && ` ${formatVorzeichen(praemie.wert_e4)}`}{" "}
        {n.einheit}
      </span>
    </div>
  );
}

/* ── Parameter ──────────────────────────────────────────────────────────── */

/**
 * Wie ein Parameter eingegeben wird. `bp` als Prozent, `kg` ganzzahlig,
 * alles andere als Betrag mit Nachkommastellen.
 */
function parse(einheit: string, text: string): number {
  if (einheit === "kg") {
    const n = Math.round(Number(text.replace(/\./g, "").replace(",", ".")));
    return Number.isFinite(n) ? n : Number.NaN;
  }
  if (einheit === "bp_pa") {
    const n = Number(text.replace(",", "."));
    return Number.isFinite(n) ? Math.round(n * 100) : Number.NaN;
  }
  return parseEuroToE4(text);
}

const zeige = (einheit: string, wert: number): string =>
  einheit === "kg"
    ? String(wert)
    : einheit === "bp_pa"
      ? formatProzent(wert)
      : formatE4(wert);

const EINHEIT_TEXT: Record<string, string> = {
  e4_je_t_weizen: "€/t Weizen",
  e4_je_t: "€/t",
  e4_je_tag: "€/Tag",
  e4_je_km: "€/km",
  e4_l_je_100km: "l/100 km",
  kg: "kg",
  bp_pa: "% p. a.",
};

function Parameterpflege({
  stamm,
  beiAenderung,
}: {
  stamm: PreisfindungStamm;
  beiAenderung: () => Promise<void>;
}) {
  const [entwurf, setEntwurf] = useState<Record<string, string>>({});
  const [meldung, setMeldung] = useState<string | null>(null);
  const [speichert, setSpeichert] = useState(false);

  const wert = (key: string, einheit: string, w: number) =>
    entwurf[key] ?? zeige(einheit, w);

  async function speichern() {
    const body: Record<string, number> = {};
    for (const z of stamm.parameter_zeilen) {
      const roh = entwurf[z.key];
      if (roh === undefined) continue;
      const n = parse(z.einheit, roh);
      if (Number.isNaN(n)) {
        setMeldung(`„${z.bezeichnung}" ist keine gültige Zahl.`);
        return;
      }
      if (n !== z.wert) body[z.key] = n;
    }
    if (!Object.keys(body).length) {
      setMeldung("Nichts geändert.");
      return;
    }
    setSpeichert(true);
    try {
      await api.patch("/verwaltung/kalkparameter", body);
      setEntwurf({});
      await beiAenderung();
      setMeldung("Gespeichert.");
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : "Speichern fehlgeschlagen.");
    } finally {
      setSpeichert(false);
    }
  }

  return (
    <div className="karte">
      <h2>Kalkulationsparameter</h2>
      <p className="hinweistext">
        Diese Werte bestimmen jeden hergeleiteten Preis. Änderungen wirken sofort auf die nächste
        Berechnung und stehen im Protokoll.
      </p>

      <div className="raster">
        {stamm.parameter_zeilen.map((z) => (
          <div className="feld" key={z.key}>
            <label htmlFor={`p-${z.key}`}>{z.bezeichnung}</label>
            <input
              id={`p-${z.key}`}
              inputMode="decimal"
              value={wert(z.key, z.einheit, z.wert)}
              onChange={(e) => setEntwurf((a) => ({ ...a, [z.key]: e.target.value }))}
            />
            <span className="hinweistext">{EINHEIT_TEXT[z.einheit] ?? z.einheit}</span>
          </div>
        ))}
      </div>

      <div className="knopfzeile">
        <button className="knopf" onClick={speichern} disabled={speichert}>
          Parameter speichern
        </button>
        {Object.keys(entwurf).length > 0 && (
          <button className="knopf sekundaer" onClick={() => setEntwurf({})}>
            Verwerfen
          </button>
        )}
        {meldung && <span className="hinweistext">{meldung}</span>}
      </div>
    </div>
  );
}
